import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Loader2, Upload, FileSpreadsheet, X, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API } from '../../lib/api';
import axios from 'axios';

export default function ImportDialog({ open, onClose, title, onImported, type }) {
  // type: 'clients' | 'contacts'
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { total, success, failed, errors }
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.toLowerCase();
    if (!ext.endsWith('.csv') && !ext.endsWith('.xlsx') && !ext.endsWith('.xls')) {
      alert('Please select a .csv or .xlsx file.');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const endpoint = type === 'clients' ? '/clients/import' : '/contacts/import';
      const res = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.success > 0) {
        onImported(res.data);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Import failed';
      setResult({ total: 0, success: 0, failed: 1, errors: [{ row: '-', message: msg }] });
    }
    setUploading(false);
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setDragOver(false);
    onClose();
  };

  if (!open) return null;

  const columns = type === 'clients'
    ? ['company_name', 'trading_name', 'abn', 'industry', 'status', 'address', 'website', 'notes']
    : ['full_name', 'company', 'client_id', 'category', 'title', 'email', 'phone', 'mobile', 'website', 'specialties', 'notes'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={handleClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: '#e5e4df' }}>
          <h3 className="text-lg font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Import {type === 'clients' ? 'Clients' : 'Contacts'}
          </h3>
          <button onClick={handleClose} className="p-1 rounded hover:bg-gray-100 text-[#6b7280]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between p-3 rounded-md" style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}>
            <div>
              <p className="text-sm font-semibold text-[#1a1a2e]">Template</p>
              <p className="text-xs text-[#6b7280]">
                Required columns: <span className="font-mono">{columns.slice(0, 3).join(', ')}, ...</span>
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Generate and download CSV template
                const header = columns.join(',');
                const sample = type === 'clients'
                  ? 'Acme Corp,Acme Trading,12345678,hospitality,lead,123 Main St,www.acme.com,Notes here'
                  : 'John Smith,Acme Corp,,client,Manager,john@acme.com,0412345678,0498765432,www.acme.com,Plumbing; Electrical,Notes here';
                const csv = `\uFEFF${header}\n${sample}`;
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `import_${type}_template.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="w-4 h-4 mr-1.5" />
              CSV Template
            </Button>
          </div>

          {/* Drop zone */}
          {!result && (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-[#c9a84c] bg-[#c9a84c]/5' : 'border-[#e5e4df] hover:border-[#c9a84c]/60'
              }`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={e => handleFile(e.target.files?.[0])}
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-[#c9a84c]" />
                  <p className="text-sm font-medium text-[#1a1a2e]">{file.name}</p>
                  <p className="text-xs text-[#6b7280]">
                    {(file.size / 1024).toFixed(1)} KB — click to change
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-[#c9a84c]/60" />
                  <p className="text-sm font-medium text-[#1a1a2e]">Drop CSV or Excel file here</p>
                  <p className="text-xs text-[#6b7280]">or click to browse</p>
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="flex items-center gap-3 p-4 rounded-lg" style={{
                background: result.failed > 0 ? '#fef3c7' : '#d1fae5',
                border: `1px solid ${result.failed > 0 ? '#f59e0b' : '#10b981'}`,
              }}>
                {result.failed === 0 ? (
                  <CheckCircle2 className="w-6 h-6 text-[#10b981] flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-[#f59e0b] flex-shrink-0" />
                )}
                <div>
                  <p className={`text-sm font-semibold ${result.failed > 0 ? 'text-[#92400e]' : 'text-[#065f46]'}`}>
                    Import Complete
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: result.failed > 0 ? '#b45309' : '#047857' }}>
                    {result.success} succeeded
                    {result.failed > 0 ? `, ${result.failed} failed (of ${result.total} total rows)` : ` (${result.total} rows)`}
                  </p>
                </div>
              </div>

              {/* Errors */}
              {result.errors?.length > 0 && (
                <div className="rounded-md" style={{ border: '1px solid #fca5a5', background: '#fef2f2' }}>
                  <div className="px-3 py-2 border-b" style={{ borderColor: '#fca5a5' }}>
                    <p className="text-xs font-semibold text-[#dc2626]">Errors ({result.errors.length})</p>
                  </div>
                  <div className="px-3 py-2 space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.slice(0, 20).map((err, i) => (
                      <p key={i} className="text-xs text-[#991b1b]">
                        <span className="font-mono text-[#dc2626]/60">Row {err.row}: </span>{err.message}
                      </p>
                    ))}
                    {result.errors.length > 20 && (
                      <p className="text-xs text-[#dc2626]/60 italic">
                        ...and {result.errors.length - 20} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* New import button */}
              <button
                onClick={() => { setResult(null); setFile(null); }}
                className="w-full text-sm text-[#3b82f6] hover:text-[#1e40af] py-1"
              >
                Import another file
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: '#e5e4df' }}>
          <Button variant="outline" onClick={handleClose}>Close</Button>
          {!result && (
            <Button
              onClick={handleImport}
              disabled={!file || uploading}
              className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Importing...' : 'Import'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
